import { Component, HostListener, OnInit } from '@angular/core';
import { OrdersCalendarSandbox } from 'app/core/sandboxes/orders-calendar.sandbox';
import { CalendarCustomer, CalendarDay, CalendarFlorderInfo } from 'app/core/store/calendar/common/calendar.interface';
import * as calendarActions from 'app/core/store/calendar/orders/calendar-orders.actions';
import * as calendarOrderActions from 'app/core/store/calendar/orders/calendar-orders.actions';
import { CalendarOrders } from 'app/core/store/calendar/orders/calendar-orders.interface';
import { SessionState } from 'app/core/store/session/session.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { logger } from 'app/core/util/logger';
import { CalendarPageContainer } from 'app/modules/calendar/common/calendar-page-container/calendar-page.container';
import { fadeInAnimation } from 'app/animations';
import { FlordersFilter } from 'app/core/interfaces/florders-filter.interface';
import * as orderDetailActions from '../../../../core/store/order-detail/order-detail.actions';
import * as templateActions from '../../../../core/store/template/template.actions';
import { goAction } from 'app/core/store/router/router.actions';
import { distinctUntilChanged, map, takeWhile } from 'rxjs/operators';
import { getFirst$$ } from 'app/core/util/util';
import { ConfirmDialogService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../../core/util/layout-breakpoints';

@Component({
  selector: 'app-calendar-orders-page',
  templateUrl: './calendar-orders-page.container.html',
  styleUrls: ['../../common/calendar-page-container/calendar-page.container.scss'],
  animations: [fadeInAnimation],
})
export class CalendarOrdersPageContainer extends CalendarPageContainer<CalendarOrders> implements OnInit /*, AfterViewInit*/ {
  busy$ = this.store.select(state => state.ui.busy);
  height: number = 0;
  isSuperUser: boolean = false;
  isReadOnly = false; // readonly: when superuser but no customer selected
  selectedCustomer = null; // for non-transporters, the selected customer
  activeCustomer = null; // for transporters (superusers) temporary active customer for loading templates and so on
  customers: Customer[] = []; // for query
  nothingFound = false;
  canViewTemplates = false;
  selectedCustomerSuccess = false;
  depots$ = this.sandbox.depots$;

  isPortrait$ = LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.portrait);
  isLandscape$ = LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.landscape);

  constructor(
    private confirmDialogService: ConfirmDialogService,
    public sandbox: OrdersCalendarSandbox,
    private breakpointObserver: BreakpointObserver,
  ) {
    super();
    logger.debug(`CalendarOrdersPageContainer constructor`);

    getFirst$$(this.sandbox.definitions$).then(definitions => {
      this.filter = {
        orderTypes: definitions.order.type.map(ot => ot.id),
      };
    });

    this.sandbox.canViewTemplates$.pipe(takeWhile(() => !this.destroyed)).subscribe((canViewTemplates: boolean) => {
      this.canViewTemplates = canViewTemplates;
    });

    this.store
      .select('session')
      .pipe(
        takeWhile(() => !this.destroyed),
        map((session: SessionState) => ({
          isSuperUser: session.userContext.isSuperUser,
          activeCustomer: session._activeCustomer.data,
          selectedCustomer: session.selectedCustomer.data,
          customers: session.customerPermissions.data.queryOrGetOrder,
          allowedTemplateUsers: session.customerPermissions.data.createOrUpdateOrder,
          selectedCustomerSuccess: session.selectedCustomer.loadSuccess,
        })),
        distinctUntilChanged()
      )
      .subscribe(({ isSuperUser, activeCustomer, selectedCustomer, customers, allowedTemplateUsers, selectedCustomerSuccess }) => {
        this.isSuperUser = isSuperUser;
        this.selectedCustomerSuccess = selectedCustomerSuccess;
        if (this.isSuperUser) {
          this.isReadOnly = !this.activeCustomer;
          this.customers = customers;
        } else {
          this.selectedCustomer = selectedCustomer;
          this.isReadOnly = false;
        }
      });

    this.store
      .select(state => state.calendar.orders)
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((calendarOrders: CalendarOrders) => {
        this.flordersState = calendarOrders;
        this.selectedTemplate = calendarOrders.selectedTemplate;
        this.deliveryDaysRequesting = calendarOrders.deliveryDaysRequesting;
        this.availableDays = calendarOrders.availableDays;
        this.nothingFound = this.flordersState.data.legend.length === 0;
        // this.customersInMonth = calendarOrders.data.legend
      });

    this.store.dispatch(new orderDetailActions.OrderDetailClearAction());

    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.contractInfo),
        distinctUntilChanged()
      )
      .subscribe(contractInfo => {
        this.orderTypes = contractInfo.orderTypes;
      });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.resize(event.currentTarget.innerHeight);
  }

  ngOnInit() {
    this.resize(window.innerHeight);
  }

  resize(innerHeight) {
    this.height = innerHeight - 205;
  }

  loadFlorders(dates: { fromDate: string; toDate: string }) {
    this.filter = {
      ...this.filter,
      unloadingDateFrom: dates.fromDate,
      unloadingDateTo: dates.toDate,
    };
    this.filterCalendar();
  }

  makeCalendarEmpty(filter?: FlordersFilter) {
    this.store.dispatch(new calendarActions.CalendarEmptyDatesAction());
  }

  private getFilter(filter?: FlordersFilter) {
    // eslint-disable-next-line
    filter = filter || this.filter;
    if (this.isSuperUser) {
      let customers;
      if (this.activeCustomer) {
        customers = [this.activeCustomer.id];
      } else {
        customers = this.customers.map(c => c.id);
      }
      // eslint-disable-next-line
      filter = {
        ...filter,
        customers,
      };
    }
    return filter;
  }

  async filterCalendar(filter?: FlordersFilter) {
    // wait for set active customer call, otherwise will bug out
    if (!this.selectedCustomerSuccess) {
      window.setTimeout(() => this.filterCalendar(filter), 100);
    } else {
      this.store.dispatch(
        new calendarActions.CalendarOrdersLoadAction({
          filter: this.getFilter(filter),
          definitions: await getFirst$$(this.sandbox.definitions$),
          places: this.places,
        })
      );
    }
  }

  async showFlorderModal(day: CalendarDay) {
    this.sandbox.showCreateOrderModal({
      day,
      readonly: (this.isSuperUser && !this.activeCustomer) || !this.authorization.ORDER.CREATE,
      activeCustomer: this.activeCustomer,
      filter: this.getFilter(),
      templates: (this.canViewTemplates && this.isSuperUser) || !this.isSuperUser ? (await getFirst$$(this.sandbox.templateState$)).templates : [],
      definitions: await getFirst$$(this.sandbox.definitions$),
      places: this.places,
    });
  }

  createFlorder() {
    if (this.authorization.ORDER.CREATE) {
      this.store.dispatch(goAction({ path: '/orders/new' }));
    }
  }

  openFlorder(order: CalendarFlorderInfo) {
    if (this.authorization.ORDER.GET) {
      this.store.dispatch(
        goAction({
          path: [`orders/${order.etmOrderNumber}`],
          query: {
            salesOrderNumber: order.salesOrderNumbers && order.salesOrderNumbers[0],
            back: 'calendar/order',
          },
        })
      );
    }
  }

  requestDeliveryDates() {
    if (this.selectedTemplate) {
      this.store.dispatch(new calendarActions.CalendarOrdersRequestOrderDeliveryDatesAction());
    }
  }

  selectTemplate(template) {
    this.store.dispatch(new calendarActions.CalendarOrdersTemplateSelectedAction(template));

    // request delivery dates for selected template when it is selected
    // not when it is unselected
    if (this.selectedTemplate) {
      this.requestDeliveryDates();
    }
  }

  createTemplate() {
    if (this.authorization.ORDER.CREATE) {
      this.store.dispatch(
        goAction({
          path: ['/orders/template/new'],
          query: { back: 'orders' },
        })
      );
    }
  }

  async createFlorderFromTemplate(templateId: number, date: string) {
    if (this.authorization.ORDER.CREATE) {
      this.sandbox.createOrderFromTemplate(this.selectedCustomer || this.activeCustomer, templateId, date);
    }
  }

  async deleteTemplate(templateId: number) {
    if (await this.confirmDialogService.askConfirmation({ message: 'CALENDAR.TEMPLATE.CONFIRM_DELETE_TEMPLATE' })) {
      this.store.dispatch(new templateActions.TemplatesDeleteAction({ templateId, type: 'ORDER' }));
    }
  }

  editTemplate(templateId: number) {
    this.store.dispatch(new templateActions.TemplatesStartEditAction({ templateId, type: 'ORDER' }));
  }

  async selectCustomer(customer: CalendarCustomer) {
    this.activeCustomer = customer;
    this.isReadOnly = false;
    await this.sandbox.loadTemplates(customer);
    if (customer.amount) {
      this.filterCalendar();
    } else {
      this.makeCalendarEmpty();
    }
  }

  async deselectCustomer() {
    this.activeCustomer = null;
    this.isReadOnly = true;
    this.filterCalendar();
    // reset if template is selected the dates are loaded, showing greyed out fields
    this.store.dispatch(new calendarOrderActions.CalendarOrdersDeliveryDatesReset());
  }
}
